import React from "react";
import useHome from "../hooks/useHome";

// components
import Button from "../../../component/element/forms/Button";

// constants and icons
import { FaPlus, FaUser } from "react-icons/fa";

// state
import { UserCompanyModal } from "../create";
import DataTable from "../../../component/element/table";
import { useAppDispatch, useAppSelector } from "../../../store";
import ItemAction from "../../../component/ui/action/ItemAction";
import { ColumnDef } from "@tanstack/react-table";
import { ICompanyUser } from "../../../store/types/user";
import { useNavigate } from "react-router-dom";
import { deleteUser } from "../../../services/apis/user";
import { twMerge } from "tailwind-merge";
import { FaShield } from "react-icons/fa6";
import { getInitials } from "../../../utility/getIntinials";
// import CreateUser from "../misc/CreateUser";

const User = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const users = useAppSelector(({ user }) => user.users);
  const companies = useAppSelector(({ company }) => company.companies);
  const loadingUserId = useAppSelector(({ user }) => user.loadingUserId);
  const last_page = useAppSelector(({ user }) => user.last_page);
  const current_page = useAppSelector(({ user }) => user.current_page);
  const { onCloseOpenModal, openD, open, setVisible, page, setPage } =
    useHome();

  const columns: ColumnDef<ICompanyUser>[] = [
    {
      accessorKey: "profile.name",
      header: "Name",
      cell: ({ getValue, row }) => {
        const value = getValue() as string;
        const profileImage = row.original.profile?.avatar;
        return (
          <div className="flex items-center gap-2">
            {profileImage ? (
              <img
                loading="lazy"
                src={profileImage}
                alt={value}
                className="h-4 w-5 rounded-full"
              />
            ) : (
              <span className="flex items-center justify-center h-8 w-8 p-1 rounded-full bg-gray-100/30 text-gray-500 text-xs font-medium">
                {getInitials(value)}
              </span>
            )}
            <span>{value}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "role",
      header: "Role",
      cell: ({ getValue }) => {
        const value = getValue() as string;
        return RoleBadge(value);
      },
    },
    {
      accessorKey: "profile.phone",
      header: "Phone Number",
    },
    {
      accessorKey: "email",
      header: "Action",
      cell: ({ getValue, row }) => {
        const email = getValue() as string;

        return (
          <ItemAction
            delete_loading={loadingUserId === email}
            onView={() => navigate(`/user/${email}`)}
            onEdit={() => navigate(`/user/edit/${email}`)}
            onDelete={() => deleteUser(email, dispatch)}
          />
        );
      },
    },
  ];

  return (
    <>
      <div className="w-full flex justify-between items-center mb-6">
        <h2 className="font-bold text-xl capitalize mb-0">All Users</h2>

        <Button
          type="button"
          title="Create"
          children={<FaPlus className="mr-2" />}
          onClick={() => setVisible(true)}
          className="px-4"
        />
      </div>

      <div className="mt-4">
        <DataTable
          columns={columns}
          data={users}
          current_page={current_page}
          last_page={last_page}
          onNext={() => page < last_page && setPage(page + 1)}
          onPrev={() => page > 1 && setPage(page - 1)}
        />
      </div>

      {/* <CreateUser visible={visible} onHandler={onCloseModal} /> */}
      <UserCompanyModal
        visible={open}
        openD={openD}
        onHandler={onCloseOpenModal}
        companies={companies}
      />
    </>
  );
};

export default User;

export const RoleBadge = (value: string) => {
  const Icon = value === "1" ? FaShield : FaUser;
  return (
    <span
      className={twMerge(
        "w-fit flex items-center gap-1 px-2 py-[2px] rounded-full leading-[10px] text-xs font-medium",
        value === "1"
          ? "bg-amber-100/30 text-amber-600"
          : "bg-blue-100/30 text-blue-600",
      )}
    >
      <Icon className="w-2.5 h-2.5" />
      {value === "1" ? "Admin" : "User"}
    </span>
  );
};
